import { useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { cardTitleTextClass, ghostBtnClass } from "@/components/common/surfaceClasses";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { inboundEventLabel } from "@/components/audience-data/audienceDataFormat";
import { AudienceDataImportCard } from "@/components/audience-data/AudienceDataImportCard";
import { AudienceDataModeSlider } from "@/components/audience-data/AudienceDataModeSlider";
import type { SsdImportJob, SsdInboundSnapshot, SsdSaleFact } from "@/lib/ssdSalesClient";

type Pane = "feed" | "import";

function formatWhen(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function NewSalesPreview({
  facts,
  newCount,
  acknowledging,
  onAcknowledgeNew,
}: {
  facts: SsdSaleFact[];
  newCount: number;
  acknowledging: boolean;
  onAcknowledgeNew: () => Promise<void>;
}) {
  return (
    <div className="min-w-0 space-y-2">
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
          New sales
        </p>
        <button
          type="button"
          className={ghostBtnClass}
          disabled={acknowledging || newCount === 0}
          onClick={() => {
            if (
              window.confirm(
                `Mark ${newCount} new sale${newCount === 1 ? "" : "s"} as reviewed? Later live arrivals will show as New again.`
              )
            ) {
              void onAcknowledgeNew();
            }
          }}
        >
          {acknowledging ? "Acknowledging…" : "Acknowledge new"}
        </button>
      </div>
      {facts.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">No new sales since last acknowledge.</p>
      ) : (
        <div className="min-w-0 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-zinc-700 dark:text-zinc-400">
                <th className="py-2 pr-3 font-medium">Date</th>
                <th className="py-2 pr-3 font-medium">Name</th>
                <th className="py-2 pr-3 font-medium">Order</th>
                <th className="py-2 font-medium">Dealer</th>
              </tr>
            </thead>
            <tbody>
              {facts.map((row) => (
                <tr key={row.id} className="border-b border-gray-100 dark:border-zinc-800">
                  <td className="whitespace-nowrap py-1.5 pr-3">{row.sale_date ?? "—"}</td>
                  <td className="py-1.5 pr-3">{row.customer_full_name || "—"}</td>
                  <td className="whitespace-nowrap py-1.5 pr-3">{row.order_number || "—"}</td>
                  <td className="py-1.5">{row.source_dealer_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AudienceDataInboundCard(props: {
  inbound: SsdInboundSnapshot;
  newSales: SsdSaleFact[];
  newCount: number;
  ingesting: boolean;
  latestJob: SsdImportJob | null;
  onFile: (file: File) => void;
  acknowledging: boolean;
  onAcknowledgeNew: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [pane, setPane] = useState<Pane>("feed");
  const events = props.inbound.events;
  const types = props.inbound.by_type;

  return (
    <SectionCard
      className="min-w-0 overflow-hidden"
      title={
        open ? (
          <div className="min-w-0 space-y-2">
            <h3 className={`text-sm font-semibold ${cardTitleTextClass}`}>Sales Feed</h3>
            <AudienceDataModeSlider
              value={pane}
              onChange={setPane}
              ariaLabel="Sales Feed or Import"
              tabs={[
                { id: "feed", label: "Sales Feed", alert: props.newCount > 0 },
                { id: "import", label: "Import" },
              ]}
            />
          </div>
        ) : (
          "Sales Feed"
        )
      }
      description={
        open
          ? pane === "feed"
            ? "Live inbound for this workspace. Acknowledge New here, or slide to Import for a CSV backfill."
            : "Upload a Sales Feed CSV. Re-import updates existing source sale ids. CSV backfill is never New."
          : undefined
      }
      rightSlot={
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          New {props.newCount}
        </button>
      }
    >
      {open ? (
      <div {...{ [FIREFLY_TARGET_ATTR]: "audience-data-inbound" }} className="min-w-0 space-y-3">
        {pane === "import" ? (
          <AudienceDataImportCard ingesting={props.ingesting} latestJob={props.latestJob} onFile={props.onFile} />
        ) : (
          <>
            <NewSalesPreview
              facts={props.newSales}
              newCount={props.newCount}
              acknowledging={props.acknowledging}
              onAcknowledgeNew={props.onAcknowledgeNew}
            />
            {types.length > 0 ? (
              <dl className="grid grid-cols-2 gap-2 text-sm text-gray-700 sm:grid-cols-3 dark:text-zinc-300">
                {types.map((row) => (
                  <div key={row.event_type} className="min-w-0">
                    <dt className="text-xs text-gray-500 dark:text-zinc-400">{inboundEventLabel(row.event_type)}</dt>
                    <dd className="font-medium">{row.count}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-sm text-gray-500 dark:text-zinc-400">
                No Sales Feed events for this workspace yet. When a subscription fires, it will show here even if it
                is not a sale.
              </p>
            )}
            {events.length > 0 ? (
              <div className="min-w-0 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-zinc-700 dark:text-zinc-400">
                      <th className="py-2 pr-3 font-medium">When</th>
                      <th className="py-2 pr-3 font-medium">Event</th>
                      <th className="py-2 pr-3 font-medium">Dealer</th>
                      <th className="py-2 pr-3 font-medium">Order</th>
                      <th className="py-2 pr-3 font-medium">Name</th>
                      <th className="py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((row) => (
                      <tr key={row.id} className="border-b border-gray-100 dark:border-zinc-800">
                        <td className="whitespace-nowrap py-1.5 pr-3">{formatWhen(row.created_at)}</td>
                        <td className="py-1.5 pr-3">{inboundEventLabel(row.event_type)}</td>
                        <td className="py-1.5 pr-3">{row.dealer_name || "—"}</td>
                        <td className="whitespace-nowrap py-1.5 pr-3">{row.order_number || "—"}</td>
                        <td className="py-1.5 pr-3">{row.subject || "—"}</td>
                        <td className="py-1.5">
                          {row.processing_status || "—"}
                          {row.error_message ? ` · ${row.error_message}` : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        )}
      </div>
      ) : null}
    </SectionCard>
  );
}
